import { ChevronLeft, ChevronRight, Close, Verified } from '@mui/icons-material';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { amazonColors } from '../../../config/amazonTheme';
import { getStaticFileUrl } from '../../../config/apiConfig';
import { PublicReviewDto } from '../../../types/review';
import { getHighlightTranslationKey } from '../../../utils/reviewHelpers';
import StarRating from './StarRating';

interface ReviewDetailModalProps {
  open: boolean;
  onClose: () => void;
  review: PublicReviewDto | null;
}

const ReviewDetailModal: React.FC<ReviewDetailModalProps> = ({
  open,
  onClose,
  review,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isImageFullscreen, setIsImageFullscreen] = useState(false);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Reset state when review changes
  useEffect(() => {
    setImageErrors(new Set());
    setCurrentPhotoIndex(0);
  }, [review?.id]);

  const handleImageError = (index: number) => {
    setImageErrors((prev) => new Set(prev).add(index));
  };

  const hasPhotos = review?.photoUrls && review.photoUrls.length > 0;
  const validPhotos = hasPhotos
    ? review.photoUrls.filter((_, index) => !imageErrors.has(index))
    : [];

  const handlePrevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : validPhotos.length - 1));
  };

  const handleNextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentPhotoIndex((prev) => (prev < validPhotos.length - 1 ? prev + 1 : 0));
  };

  if (!review) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        className="notranslate"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
          },
          translate: 'no' as any,
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {review.reviewerName}
            </Typography>
            {review.isVerified && (
              <Verified
                sx={{
                  fontSize: 20,
                  color: amazonColors.orange,
                }}
              />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.5}>
            {/* Rating & Date */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <StarRating value={review.rating} readonly size="medium" />
              <Typography variant="body2" color="text.secondary">
                {formatDate(review.createdAt)}
              </Typography>
            </Box>

            {/* Verified Badge */}
            {review.isVerified && (
              <Chip
                label={t('reviews.verifiedPurchase')}
                size="small"
                sx={{
                  width: 'fit-content',
                  bgcolor: 'rgba(255, 153, 0, 0.1)',
                  color: amazonColors.orange,
                  fontWeight: 500,
                }}
              />
            )}

            {/* Photos Grid/Carousel */}
            {validPhotos.length > 0 && (
              <Box>
                {/* Photo thumbnails grid */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: validPhotos.length === 1 ? '1fr' : 'repeat(auto-fill, minmax(100px, 1fr))',
                    gap: 1,
                  }}
                >
                  {validPhotos.map((photoUrl, index) => (
                    <Box
                      key={index}
                      onClick={() => {
                        setCurrentPhotoIndex(index);
                        setIsImageFullscreen(true);
                      }}
                      sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        aspectRatio: validPhotos.length === 1 ? 'auto' : '1',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.grey[200]}`,
                        '&:hover': {
                          opacity: 0.9,
                          borderColor: amazonColors.orange,
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={getStaticFileUrl(photoUrl)}
                        alt={`Review photo ${index + 1}`}
                        onError={() => handleImageError(review.photoUrls.indexOf(photoUrl))}
                        sx={{
                          width: '100%',
                          height: validPhotos.length === 1 ? 'auto' : '100%',
                          maxHeight: validPhotos.length === 1 ? 300 : 'none',
                          objectFit: 'cover',
                          bgcolor: theme.palette.grey[100],
                        }}
                      />
                    </Box>
                  ))}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  {t('reviews.clickToEnlarge')}
                </Typography>
              </Box>
            )}

            {/* Comment */}
            {review.comment && (
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    lineHeight: 1.7,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {review.comment}
                </Typography>
              </Box>
            )}

            {/* Highlights */}
            {review.highlights && review.highlights.length > 0 && (
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  {t('reviews.whatTheyLiked')}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {review.highlights.map((highlight) => (
                    <Chip
                      key={highlight}
                      label={t(`reviews.highlights.${getHighlightTranslationKey(highlight)}`)}
                      size="small"
                      sx={{
                        bgcolor: theme.palette.grey[100],
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Would Order Again */}
            {review.wouldOrderAgain && (
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  {t('reviews.wouldOrderAgainLabel')}
                </Typography>
                <Chip
                  label={t(`reviews.orderAgain.${review.wouldOrderAgain.toLowerCase()}`)}
                  size="small"
                  color={review.wouldOrderAgain === 'Yes' ? 'success' : review.wouldOrderAgain === 'Maybe' ? 'warning' : 'error'}
                />
              </Box>
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Image Lightbox with navigation */}
      {isImageFullscreen && validPhotos.length > 0 && (
        <Box
          onClick={() => setIsImageFullscreen(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={() => setIsImageFullscreen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <Close />
          </IconButton>

          {/* Previous button */}
          {validPhotos.length > 1 && (
            <IconButton
              onClick={handlePrevPhoto}
              sx={{
                position: 'absolute',
                left: 16,
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <ChevronLeft fontSize="large" />
            </IconButton>
          )}

          {/* Image */}
          <Box
            component="img"
            src={getStaticFileUrl(validPhotos[currentPhotoIndex])}
            alt={`Review photo ${currentPhotoIndex + 1}`}
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 1,
            }}
          />

          {/* Next button */}
          {validPhotos.length > 1 && (
            <IconButton
              onClick={handleNextPhoto}
              sx={{
                position: 'absolute',
                right: 16,
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <ChevronRight fontSize="large" />
            </IconButton>
          )}

          {/* Photo counter */}
          {validPhotos.length > 1 && (
            <Typography
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                px: 2,
                py: 0.5,
                borderRadius: 2,
                fontSize: '0.875rem',
              }}
            >
              {currentPhotoIndex + 1} / {validPhotos.length}
            </Typography>
          )}
        </Box>
      )}
    </>
  );
};

export default React.memo(ReviewDetailModal);
